/**
 * A helper that gets the last definition of a meta key on a specific route.
 * This is useful because route.meta can be an Array.
 * https://router.vuejs.org/guide/advanced/meta.html#route-meta-fields
 *
 * @param {object} route - Vue Router route instance.
 * @param {string} key - A key from a meta object matched against the meta object of the route.
 * @returns {*} - The value of the meta key on the route.
 */
const metaFinder = (route: any, key: string) => {
  // Fetch the last matching meta value from the list of route records
  if (Array.isArray(route.meta))
    return route.meta.reduce((previous: any, current: any) => current[key] || previous, undefined)

  // Simply get the meta value because only one route is matching
  return route.meta[key]
}

/**
 * Navigation guard collection around authenticated state.
 *
 * @param {object} context - `store` and `route` access.
 * @returns {string|undefined} - The route to redirect to or nothing
 */
const authGuards = ({ userStore, navigationStore, to }: any) => {
  // Assess whether user is authenticated or not
  const isUserAuthenticated = userStore.isUserLoged

  // Assess whether route is public or not
  const isPagePublic = metaFinder(to, 'isPublic')

  // if router is public don't want to show login components
  const noAuthRequest = metaFinder(to, 'noAuthRequest')
  if (noAuthRequest) {
    navigationStore.setEmbedContext()
  }

  // Assess whether route is exclusive to unauthenticated user or not
  const onlyUnauthenticated = metaFinder(to, 'onlyUnauthenticated')

  // Redirect to the user page if the authenticated user tries to access a page it shouldn't
  if (onlyUnauthenticated && isUserAuthenticated) return '/'
  // Skip redirections if the page is public and the user isn't authenticated
  else if (isPagePublic || to.path.startsWith('/auth/psc') || to.path.startsWith('/auth/france')) return

  // Redirect to the signin page in case the user tries to access a private page while being unauthenticated
  if (!isPagePublic && !isUserAuthenticated) {
    navigationStore.addTempPath(to.path === '/logout' ? '/patients' : to.path)
    return '/'
  }
}

/**
 * Navigation guard collection around administrator state.
 *
 * @param {object} context - `store` and `route` access.
 * @returns {string|undefined} - The route to redirect to or nothing
 */
const roleGuards = ({ userStore, navigationStore, to }: any) => {
  const neededRoles = metaFinder(to, 'neededRoles')
  const userRoles = userStore.roles
  // Redirect to user page in case the user tries to access a restricted page while being unauthorized
  if (neededRoles && neededRoles.length) {
    for (const role of neededRoles) {
      if (!userRoles.includes(role)) {
        return '/user'
      }
    }
  }
}

export default defineNuxtRouteMiddleware(async (to, from) => {
  const userStore = useUserStore()
  const navigationStore = useNavigationStore()
  const guardResults = [authGuards, roleGuards].map((guardCollection) => guardCollection({ userStore, navigationStore, to }))

  // Parse every navigation guards results and redirect without continuing if necessary
  for (const guardResult of guardResults) if (guardResult) return navigateTo(guardResult)
})
